import { useAuth, useClerk } from '@clerk/react';
import { useEffect } from 'react';

interface ClerkAuthBridge {
  loaded: boolean;
  getToken: () => Promise<string | null>;
  signOut: () => Promise<void>;
}

let authBridge: ClerkAuthBridge | null = null;
const bridgeWaiters = new Set<() => void>();

export async function getClerkToken(): Promise<string | null> {
  if (!authBridge?.loaded) {
    await new Promise<void>((resolve) => {
      const ready = () => {
        if (!authBridge?.loaded) return;
        clearTimeout(timeout);
        bridgeWaiters.delete(ready);
        resolve();
      };
      const timeout = setTimeout(() => {
        bridgeWaiters.delete(ready);
        resolve();
      }, 15_000);
      bridgeWaiters.add(ready);
      ready();
    });
  }
  return authBridge?.getToken() ?? null;
}

export async function signOutOfClerk(): Promise<void> {
  await authBridge?.signOut();
}

export function ClerkAuthBridge() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const { signOut } = useClerk();

  useEffect(() => {
    authBridge = {
      loaded: isLoaded,
      getToken: async () => {
        if (!isLoaded || !isSignedIn) return null;
        return getToken();
      },
      signOut: async () => {
        if (isLoaded && isSignedIn) await signOut();
      },
    };
    for (const ready of bridgeWaiters) ready();

    const handleUnauthorized = () => {
      void signOut();
    };
    window.addEventListener('residenone:auth:logout', handleUnauthorized);

    return () => {
      window.removeEventListener('residenone:auth:logout', handleUnauthorized);
      authBridge = null;
    };
  }, [getToken, isLoaded, isSignedIn, signOut]);

  return null;
}
